import { Component, Input, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  Participant, Preference, PREFERENCE_LABELS, SessionGroup, SplitType, peaksFor,
} from '../../../invitations/models/invitation.model';
import { runSplit } from '../../services/session-split';

/** One selectable split-type card. Copied from the old `session-create-modal` combobox options,
 *  reshaped into the card format so this step matches the Reports wizard's report-type picker. */
interface SplitCard {
  type: SplitType;
  title: string;
  desc: string;
}

/**
 * Both splits are deterministic, so this preview is exact: the groups shown here are the groups
 * that get saved. It used to be only a sample of the algorithm's behaviour, because the lottery
 * split re-shuffled on save — that split is gone (v16.2.082) and the caveat with it.
 */
@Component({
  selector: 'app-session-step-split',
  templateUrl: './step-session-split.component.html',
  styleUrls: ['./step-session-split.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SessionStepSplitComponent {
  @Input() form!: FormGroup;
  @Input() previewMembers: Participant[] = [];

  readonly prefLabels = PREFERENCE_LABELS;

  readonly splitOptions: SplitCard[] = [
    { type: 'similar', title: 'Similar Preference', desc: 'People who share a peak set sit together, packed into your chosen number of groups.' },
    { type: 'diverse_exact', title: 'Diverse Preference: Exact', desc: 'Deals every preference bucket evenly across your chosen number of groups.' },
  ];

  get splitType(): SplitType { return this.form.value.splitType; }
  get groupCount(): number { return this.form.value.groupCount; }

  /** Both splits honour the requested count, so the field always applies. */
  get showGroupCount(): boolean { return true; }

  get preview(): SessionGroup[] {
    return runSplit(this.previewMembers, this.splitType, this.groupCount);
  }

  isSelected(t: SplitType): boolean { return this.splitType === t; }

  selectType(t: SplitType): void { this.form.get('splitType')!.setValue(t); }

  onGroupCountChange(value: number): void { this.form.get('groupCount')!.setValue(value); }

  membersOf(group: SessionGroup): Participant[] {
    const byId = new Map(this.previewMembers.map(m => [m.id, m]));
    return group.memberIds.map(id => byId.get(id)).filter((m): m is Participant => !!m);
  }

  /** app-fs-icon takes a `profile` object shaped like the API's — `profile_type_id`, not camelCase. */
  iconProfile(p: Participant): { profile_type_id: number | undefined } {
    return { profile_type_id: p.profileTypeId };
  }

  peaks(p: Participant): Preference[] { return peaksFor(p.profileTypeId); }
}
